import type { CSSProperties } from 'react'
import { VpEarthIcon } from '@vtmn-play/icons/react'
import { VpActionChip } from '@vtmn-play/react'
import { useTranslation } from 'react-i18next'
import { useLocation, useParams, useSearchParams } from 'react-router'
import { CountrySelectorModal } from '@/components/CountrySelectorModal/CountrySelectorModal'
import { FavoriteViewAction } from '@/components/FavoriteViews/FavoriteViewAction'
import { InfoTooltip } from '@/components/ProductsGallery/InfoTooltip'
import { useCountriesContext } from '@/contexts/countries/useCountriesContext'
import { buildFavoriteViewSearch } from '@/contexts/favorites/favoriteView'
import './products-header-action-chips.scss'

export function ProductsHeaderActionChips({ isProductsTabActive }: Readonly<{ isProductsTabActive: boolean }>) {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const { season, universeId, departmentId, subdepartmentId } = useParams()
  const [searchParams] = useSearchParams()
  const {
    countries,
    totalCountries,
    isCountriesError,
    isCountriesLoading,
    isOpen,
    setIsOpen,
    getCountryByCode,
    normalizeCountryCodes,
  } = useCountriesContext()
  const viewMode = searchParams.get('viewMode') ?? 'sm'
  const isCountrySelectionUnavailable = isProductsTabActive || viewMode === 'sm'

  const selectedCountryCodes = normalizeCountryCodes(searchParams.getAll('homologation_countries'))
  const selectedCountry = selectedCountryCodes.length === 1
    ? getCountryByCode(selectedCountryCodes[0])
    : undefined
  const currentSearch = buildFavoriteViewSearch({
    season,
    universeId,
    departmentId,
    subdepartmentId,
    pathname,
    searchParams,
    countries,
  })
  let countryChipLabel: string
  if (selectedCountryCodes.length === 1) {
    countryChipLabel = selectedCountry?.name ?? t('products-page.n-countries-selected', { count: 1 })
  }
  else if (selectedCountryCodes.length === 0 || (totalCountries > 0 && selectedCountryCodes.length === totalCountries)) {
    countryChipLabel = t('products-page.all-countries')
  }
  else {
    countryChipLabel = t('products-page.n-countries-selected', { count: selectedCountryCodes.length })
  }

  const countrySelectorChip = (triggerProps: { 'aria-describedby'?: string, 'style'?: CSSProperties } = {}) => (
    <VpActionChip
      {...triggerProps}
      data-testid="country-selector-chip"
      id="product-gallery-country-selector"
      className={isCountrySelectionUnavailable ? 'country-selector-chip--unavailable' : undefined}
      aria-disabled={isCountrySelectionUnavailable}
      onClick={() => {
        if (!isCountrySelectionUnavailable) {
          setIsOpen(true)
        }
      }}
    >
      <VpEarthIcon />
      {countryChipLabel}
    </VpActionChip>
  )

  return (
    <div className="products-header-buttons" data-testid="products-header-buttons">
      <FavoriteViewAction search={currentSearch} />
      {isCountrySelectionUnavailable
        ? (
            <InfoTooltip
              content={t('products-page.country-selection-unavailable')}
              tooltipClassName="country-selector-tooltip--offset"
            >
              {triggerProps => countrySelectorChip(triggerProps)}
            </InfoTooltip>
          )
        : countrySelectorChip()}

      <CountrySelectorModal
        countries={countries}
        hasCountriesError={isCountriesError}
        isOpen={isOpen}
        isLoadingCountries={isCountriesLoading}
        onClose={() => setIsOpen(false)}
      />
    </div>
  )
}
